import { Component, forwardRef, Input, OnChanges } from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';

import { AccountLicenseService } from 'rev-shared/security/AccountLicense.Service';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';

import { AttendeeJoinMethod, EventAccessControl } from 'rev-portal/scheduledEvents/EventAccessControl';
import { EventRegistrationPreviewDialog } from 'rev-portal/scheduledEvents/eventRegistration/Dialog.Config';
import { InsightSearchHelper } from 'rev-portal/search/InsightSearchHelper.Service';
import { ViewerIdPolicy } from 'rev-shared/viewerId/ViewerIdContract';
import { WebcastType } from 'rev-portal/scheduledEvents/webcast/WebcastType';

import { EditWebcastModel } from './EditWebcastModel';
import { EditWebcastBaseSettingsComponent } from './EditWebcastBaseSettings.Component';
import { TranslateService } from '@ngx-translate/core';

import styles from './EditWebcast.module.less';

@Component({
	selector: 'edit-webcast-attendee-settings',
	templateUrl: './EditWebcastAttendeeSettings.Component.html',
	providers: [{
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => EditWebcastAttendeeSettingsComponent),
		multi: true,
	}],
	standalone: false
})
export class EditWebcastAttendeeSettingsComponent extends EditWebcastBaseSettingsComponent implements OnChanges {
	@Input() public webcast: EditWebcastModel;
	@Input() public features: IMediaFeatures;
	@Input() public isSidebar: boolean;
	@Input() public hidden: boolean;

	public readonly EventAccessControl = EventAccessControl;
	public readonly accountId: string = this.UserContext.getAccount().id;
	public readonly styles = styles;
	public readonly AttendeeJoinMethod = AttendeeJoinMethod;
	public readonly ViewerIdPolicy = ViewerIdPolicy;

	public accessControlInsightOptions: any;
	public displayPublicHoursInvalidWarning: boolean;
	public hasSetEventPublicRights: boolean;
	public initialAccessControl: EventAccessControl;

	constructor(
		private readonly accountLicense: AccountLicenseService,
		private readonly DialogService: DialogService,
		private readonly InsightSearchHelper: InsightSearchHelper,
		private readonly UserContext: UserContextService,
		private readonly translateService: TranslateService,
		SecurityContext: SecurityContextService
	) {
		super();
		this.hasSetEventPublicRights = SecurityContext.checkAuthorization('events.setEventPublic');
	}

	public ngOnChanges(): void {
		if(this.webcast && this.features) {
			this.initInsightOptions();
			this.validatePublicHours();
			this.initialAccessControl = this.webcast.accessControl;
			this.webcast.attendeeJoinMethod = this.webcast.attendeeJoinMethod || AttendeeJoinMethod.Registration;
			this.webcast.viewerIdWatermarkText = this.webcast.viewerIdWatermarkText?.length ? this.webcast.viewerIdWatermarkText : this.features?.viewerIdSettings?.customInformation;
		}
	}

	public get publicListingVisible(): boolean {
		const hasRightToSetEventPublicOrAlreadyPublic = this.hasSetEventPublicRights || this.initialAccessControl === EventAccessControl.Public;
		return (
			this.features?.enablePublicWebcastAccess &&
			this.accountLicense.viewingHoursValid &&
			hasRightToSetEventPublicOrAlreadyPublic &&
			(!this.webcast.cloneWebcastId || this.hasSetEventPublicRights)
		);
	}

	public get trustedPublicListingVisible(): boolean {
		const enableTrustedPublic = this.features?.enableTrustedPublicWebcastAccess || this.webcast.webcastType == WebcastType.WebexEvents;
		const hasRightToSetEventPublicOrAlreadyPublic = (this.hasSetEventPublicRights || this.initialAccessControl === EventAccessControl.TrustedPublic);
		return enableTrustedPublic && hasRightToSetEventPublicOrAlreadyPublic && (!this.webcast.cloneWebcastId || this.hasSetEventPublicRights);
	}

	public initInsightOptions(): void {
		if(this.accessControlInsightOptions) {
			return;
		}

		const { accountId, webcast } = this;
		this.accessControlInsightOptions = this.InsightSearchHelper.buildAccessEntityInsightOptions({
			accountId,
			users: true,
			groups: true,
			getEntityIds: () => webcast.userIds.concat(webcast.groupIds)
		});
	}

	private validatePublicHours(): void {
		this.displayPublicHoursInvalidWarning =
			this.webcast.accessControl === EventAccessControl.Public &&
			!this.accountLicense.viewingHoursValid;

	}

	public onAccessControlChanged(): void {
		this.validatePublicHours();
		this.assignAccessJoinMethod();
		this.assignConsentVerbiage();
	}

	public assignAccessJoinMethod(): void {
		this.webcast.attendeeJoinMethod = this.webcast.accessControl === EventAccessControl.Public
			? AttendeeJoinMethod.Registration : undefined;
	}

	public onAttendeeJoinMethodChange(): void {
		if(this.features.enableWebcastCustomConsent && this.webcast.attendeeJoinMethod === AttendeeJoinMethod.Anonymous) {
			this.webcast.isCustomConsentEnabled = false;
		}
	}

	public previewGuestLogin(): void {
		this.DialogService.getDialog(EventRegistrationPreviewDialog).open({
			initialState: {
				webcast: this.webcast
			}
		});
	}

	private assignConsentVerbiage(): void
	{
		if(this.webcast.accessControl === EventAccessControl.Public && this.features.enableWebcastCustomConsent
			|| this.webcast.isCustomConsentEnabled) {
			this.webcast.isCustomConsentEnabled = false;
		}
	}

	public onCustomizeConsentVerbiageChanged(): void {
		if(this.webcast.isCustomConsentEnabled) {
			const params = {
				title: this.translateService.instant('Event_Registration_EnableCustomizeConsentVerbiageDisclaimer_Title'),
				message: this.translateService.instant('Event_Registration_EnableCustomizeConsentVerbiageDisclaimer'),
				actionText: this.translateService.instant('Ok'),
				cancelText: this.translateService.instant('Cancel')
			};

			this.DialogService.openConfirmationDialog(params).result
				.then(() => { this.webcast.consentVerbiage = '';})
				.catch(() => {this.webcast.isCustomConsentEnabled = false;});
		}
	}
}
